import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  MutableRefObject,
} from 'react';
import {mergeRefs} from '@packages/systems/design-system/utils';
import {reportBug} from '@packages/utilities/reporter';
import useResizeObserver from '@packages/systems/ui/hooks/useResizeObserver';
import {useWebflowSiteModules} from './useWebflowSiteModules';

interface RiveElementProps {
  stateMachine: string;
  artboard: string;
  src: string;
  autoPlay: true;
  isTouchScrollEnabled: boolean;
  automaticallyHandleEvents: boolean;
  fit: string;
  alignment: string;
  container: HTMLElement;
  isPreviewMode: boolean;
  isEditingIx2: boolean;
  moduleInstanceRef: MutableRefObject<null | any>;
}

const getFrontendModule = (el: HTMLElement, moduleName: 'rive') => {
  // @ts-expect-error - TS18047 - 'el.ownerDocument.defaultView' is possibly 'null'. | TS2339 - Property 'Webflow' does not exist on type 'Window & typeof globalThis'.
  const webflow = el.ownerDocument.defaultView.Webflow;
  if (!webflow || !webflow.require) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug(
      'GlobalWebflowObject',
      'Global webflow object is uninstantiated.',
      el.ownerDocument.defaultView
    );
  }
  const result = webflow.require?.(moduleName);

  return result;
};

const usePrevious = (
  value: boolean | number,
  initial: null | boolean = null
) => {
  const ref = useRef<boolean | number | null>(initial);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const RiveElement = forwardRef(
  (
    {
      stateMachine = '',
      artboard = '',
      src = '',
      autoPlay = true,
      isTouchScrollEnabled = false,
      automaticallyHandleEvents = false,
      fit = '',
      alignment = '',
      container,
      isPreviewMode,
      isEditingIx2,
      moduleInstanceRef,
      ...props
    }: RiveElementProps,
    ref: React.ForwardedRef<HTMLElement>
  ) => {
    const canvasRef = useRef(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [currentSrc, setCurrentSrc] = useState('');
    const [currentArtboard, setCurrentArtboard] = useState('');
    const [currentStateMachine, setCurrentStateMachine] = useState('');
    const [currentFit, setCurrentFit] = useState('');
    const [currentAlignment, setCurrentAlignment] = useState('');
    const [currentAutoPlay, setCurrentAutoPlay] = useState(true);
    const [currentIsTouchScrollEnabled, setCurrentIsTouchScrollEnabled] =
      useState(false);
    const [
      currentAutomaticallyHandleEvents,
      setCurrentAutomaticallyHandleEvents,
    ] = useState(false);
    const wasEditingIx2 = usePrevious(isEditingIx2, false);

    const [reloadTime, setReloadTime] = useState(0);
    const lastReloadTime = usePrevious(reloadTime);
    const webflowSiteModules = useWebflowSiteModules(container);

    useEffect(() => {
      if (!container) return;
      const handleRiveReload = () => setReloadTime(Date.now());
      container.addEventListener('RELOAD_RIVE', handleRiveReload);
      return () => {
        container.removeEventListener('RELOAD_RIVE', handleRiveReload);
      };
    }, [container]);

    // Initialize runtime when component is mounted
    useEffect(() => {
      setIsLoading(true);

      let moduleInstance;
      const containerExists = container && canvasRef.current;
      const ix2Changed = isEditingIx2 !== wasEditingIx2;
      const srcChanged = src !== currentSrc;
      const artboardChanged = artboard !== currentArtboard;
      const fitChanged = fit !== currentFit;
      const alignmentChanged = alignment !== currentAlignment;
      const stateMachineChanged = stateMachine !== currentStateMachine;
      const autoPlayChanged = autoPlay !== currentAutoPlay;
      const isTouchScrollEnabledChanged =
        isTouchScrollEnabled !== currentIsTouchScrollEnabled;
      const automaticallyHandleEventsChanged =
        automaticallyHandleEvents !== currentAutomaticallyHandleEvents;
      const reloadTimeChanged = reloadTime !== lastReloadTime;

      if (
        containerExists &&
        (ix2Changed || reloadTimeChanged) &&
        webflowSiteModules
      ) {
        const frontend = getFrontendModule(container, 'rive');
        if (frontend) frontend.destroyInstance(container);
      }

      if (
        containerExists &&
        webflowSiteModules &&
        (ix2Changed ||
          srcChanged ||
          artboardChanged ||
          stateMachineChanged ||
          autoPlayChanged ||
          isTouchScrollEnabledChanged ||
          fitChanged ||
          alignmentChanged ||
          automaticallyHandleEventsChanged ||
          reloadTimeChanged)
      ) {
        init();
      }

      async function init() {
        moduleInstance = await createRiveInstance();

        if (!moduleInstance) {
          throw Error('Could not init rive', src as ErrorOptions);
        }

        moduleInstanceRef.current = moduleInstance;

        setIsLoading(false);
        setCurrentSrc(src);
        setCurrentArtboard(artboard);
        setCurrentStateMachine(stateMachine);
        setCurrentAutoPlay(autoPlay);
        setCurrentIsTouchScrollEnabled(isTouchScrollEnabled);
        setCurrentAutomaticallyHandleEvents(automaticallyHandleEventsChanged);
        setCurrentFit(fit);
        setCurrentAlignment(alignment);
      }

      return () => {
        resizeObserver.unobserve(container);
      };
      // Note: it's important that this hook only updates on specific deps
      // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isPreviewMode,
      isEditingIx2,
      artboard,
      stateMachine,
      src,
      reloadTime,
      webflowSiteModules,
    ]);

    const createRiveInstance = async () => {
      const frontend = getFrontendModule(container, 'rive');
      if (!frontend) return;

      const riveLoaded = () => {
        resizeObserver.observe(container);
      };

      const moduleInstance = await frontend.createInstance({
        container,
        src,
        stateMachine,
        artboard,
        onLoad: riveLoaded,
        autoplay: isPreviewMode && autoPlay,
        isTouchScrollEnabled,
        automaticallyHandleEvents,
        fit,
        alignment,
      });

      return moduleInstance;
    };

    const resizeObserver = useResizeObserver(async () => {
      if (!container || isPreviewMode) return;

      const frontend = getFrontendModule(container, 'rive');
      if (!frontend) return;

      const {rive, riveInstanceSuccessLoaded} =
        frontend.getInstance(container) || {};

      if (rive && riveInstanceSuccessLoaded) {
        rive?.resizeDrawingSurfaceToCanvas();
      }
    });

    return (
      <canvas
        data-automation-id="rive-canvas-element"
        ref={mergeRefs([ref, canvasRef])}
        style={{
          display: isLoading ? 'none' : 'block',
          height: '100%',
          width: '100%',
        }}
        {...props}
      />
    );
  }
);

export default RiveElement;
